import type { LabelZone } from '../views/LabelsView';

/**
 * Reading a fresh label and PROPOSING the zones — never setting them.
 *
 * Marking up a label by hand is a dozen careful drags, and the boring part is
 * not deciding what matters, it is placing the rectangle. So the machine places
 * rectangles and a person decides.
 *
 * WHY THIS IS A PROPOSAL AND NOT AN ANSWER, structurally.
 *
 * The failure here is not "the box is 8 px off" — a person drags it. It is a
 * zone that looks right and checks the wrong thing: a lot code typed as "must
 * match" passes the approved sample and then rejects every real pack, because
 * the code changes by design. That mistake is invisible at review time and
 * obvious at 6am on the line.
 *
 * So nothing this returns is saved. Proposals arrive in their own list, drawn
 * dashed, and become zones only when somebody accepts them. That is the same
 * bargain readLabels.ts already makes with chart names, for the same reason.
 *
 * TWO DETECTORS, BECAUSE THEY FAIL DIFFERENTLY.
 *
 * Codes are decoded, not guessed: a barcode either resolves to digits or it
 * does not, so a proposed `code` zone comes with the value it actually read and
 * is worth trusting. Text is OCR on whatever the camera got — on a curved
 * bottle shot at an angle, most of it will be wrong. That asymmetry is carried
 * through to the UI as a confidence, so the two are not offered as equals.
 */

export interface ZoneProposal extends LabelZone {
  /** What was actually read there. Shown beside the box so a bad read is visible. */
  readText: string;
  /** 0-100. Codes decode or they don't, so they arrive at 100; OCR rarely does. */
  confidence: number;
  /** Why it was typed the way it was, in words, e.g. "looks like a date". */
  reason: string;
}

/** A word as Tesseract located it, in image pixels. */
interface Word {
  text: string;
  conf: number;
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  /** Which orientation read it. Words read at different angles never share a line. */
  deg: number;
  /**
   * The same word in the frame it was READ in.
   *
   * Lines are found by "shares a baseline, sits close horizontally", and that
   * is only true in the reading frame: print turned ninety degrees shares an x
   * in the original image, not a y. Grouping on the original coordinates puts
   * every vertical line's words in separate blocks and produces one tiny zone
   * per word. The image-frame box is still what a zone is drawn from.
   */
  rx0: number;
  ry0: number;
  rx1: number;
  ry1: number;
}

/**
 * How a field is recognised and what check it should get.
 *
 * Order matters: the first match wins, so the specific patterns (a date, a lot
 * code) come before the general ones. Every entry says what it is FOR, because
 * the difference between `match` and `present` is the whole point — `match`
 * means this print must be identical, `present` means ink must be here but the
 * content changes every pack.
 */
const FIELD_RULES: Array<{
  test: RegExp;
  label: string;
  check: LabelZone['check'];
  pattern?: string;
  reason: string;
}> = [
  {
    // A date is the field most often wrong on a real pack and the one that must
    // never be typed `match`: it changes every run by definition.
    test: /\b(THT|TGT|BBE|EXP|BEST\s*(BEFORE|VOOR)|HOUDBAAR|USE\s*BY)\b/i,
    label: 'Best-before date',
    check: 'text',
    pattern: '\\d{2}[-./]\\d{2}[-./]\\d{2,4}',
    reason: 'names a best-before date',
  },
  {
    test: /\b\d{1,2}[-./]\d{1,2}[-./]\d{2,4}\b/,
    label: 'Date',
    check: 'text',
    pattern: '\\d{1,2}[-./]\\d{1,2}[-./]\\d{2,4}',
    reason: 'looks like a date',
  },
  {
    test: /\b(LOT|BATCH|CHARGE|L\.?\s?\d)/i,
    label: 'Lot code',
    check: 'present',
    reason: 'looks like a lot or batch code — changes per run, so ink must be there, not identical',
  },
  {
    test: /\b\d+([.,]\d+)?\s*(kg|g|ml|cl|l|fl\.?\s?oz|oz|lb)\b/i,
    label: 'Net contents',
    check: 'text',
    pattern: '\\d+([.,]\\d+)?\\s*(kg|g|ml|cl|l|fl\\.?\\s?oz|oz|lb)',
    reason: 'looks like a weight or volume',
  },
  {
    test: /\b(www\.|https?:\/\/|\.com|\.nl|\.eu)\b/i,
    label: 'Website',
    check: 'match',
    reason: 'looks like a web address — fixed print',
  },
  {
    /*
      Phone-shaped, not merely digits-with-a-dash. The greedy earlier version
      (`\+?\d[\d\s-]{7,}\d`) confidently labelled the product code "2025-01899"
      a phone number — measured on a real bottle. A phone number in the wild has
      a country prefix, a word-dialled form, or three-plus separated groups;
      require one of those so a batch code falls through to plain printed text
      instead.
    */
    test: /(\+\d[\d\s-]{7,}\d)|(\b\d{3,4}[-\s.]\d{3}[-\s.]\d{3,4}\b)|(\b\d\s?[-.]?\s?\d{3}[-\s.][A-Z]{4,})/,
    label: 'Phone number',
    check: 'match',
    reason: 'looks like a phone number — fixed print',
  },
  {
    test: /\b(INGREDI|SAMENSTELLING|CONTAINS|ALLERG)/i,
    label: 'Ingredients',
    check: 'match',
    reason: 'names ingredients — legally fixed text',
  },
  {
    test: /\b(RECYCL|PLASTIC|BOTTLE|PMD|STATIEGELD)\b/i,
    label: 'Recycling mark',
    check: 'match',
    reason: 'a recycling mark — fixed print',
  },
];

/** Codes are their own kind of field and never go through the text rules. */
function codeLabel(format: string): string {
  if (/QR/i.test(format)) return 'QR code';
  if (/EAN|UPC/i.test(format)) return `Barcode (${format.replace(/_/g, '-')})`;
  return `Code (${format.replace(/_/g, '-')})`;
}

/**
 * Find the codes, by decoding them.
 *
 * zxing is bundled, not fetched — same rule as the OCR data. A plant network
 * with no route out must not be the thing that breaks this.
 *
 * FOUR ROTATIONS, AND THAT IS NOT BELT-AND-BRACES.
 *
 * Measured on a real photograph of a water bottle: at 0° the whole image
 * decoded NOTHING. The same image at 90° gave the UPC-A, at 180° gave the QR.
 * zxing scans horizontal lines, so a barcode printed down the side of a bottle
 * is invisible to it until the image is turned — and the QR failed at 0° only
 * because of how the binariser happened to sample it. One orientation is not a
 * detector, it is a coin flip.
 *
 * Everything is scanned at 2x too. The photo that prompted this was 480x640,
 * which puts the QR at about a hundred pixels; upscaling costs a few
 * milliseconds and is the difference between finding it and not.
 */
async function detectCodes(canvas: HTMLCanvasElement): Promise<ZoneProposal[]> {
  const {
    MultiFormatReader,
    BarcodeFormat,
    DecodeHintType,
    HybridBinarizer,
    BinaryBitmap,
    HTMLCanvasElementLuminanceSource,
  } = await import('@zxing/library');

  const hints = new Map();
  hints.set(DecodeHintType.TRY_HARDER, true);

  const w = canvas.width;
  const h = canvas.height;
  const found: ZoneProposal[] = [];
  const seen = new Set<string>();

  for (const deg of [0, 90, 180, 270]) {
    const rad = (deg * Math.PI) / 180;
    const rotated = document.createElement('canvas');
    const scale = 2;
    const sw = w * scale;
    const sh = h * scale;
    rotated.width = deg % 180 === 0 ? sw : sh;
    rotated.height = deg % 180 === 0 ? sh : sw;

    const ctx = rotated.getContext('2d')!;
    ctx.translate(rotated.width / 2, rotated.height / 2);
    ctx.rotate(rad);
    ctx.drawImage(canvas, -sw / 2, -sh / 2, sw, sh);

    /**
     * Undo the transform above, so a point zxing reports in the rotated canvas
     * comes back as a fraction of the original image. Getting this wrong does
     * not fail loudly — it draws a confident box over the wrong part of the
     * label — so it is derived from the same numbers used to draw, rather than
     * special-cased per angle.
     */
    const unrotate = (xr: number, yr: number) => {
      const dx = xr - rotated.width / 2;
      const dy = yr - rotated.height / 2;
      const cos = Math.cos(-rad);
      const sin = Math.sin(-rad);
      return {
        x: (dx * cos - dy * sin + sw / 2) / sw,
        y: (dx * sin + dy * cos + sh / 2) / sh,
      };
    };

    /**
     * Decode, mask out what was found, decode again.
     *
     * zxing ships GenericMultipleBarcodeReader for exactly this, and in 0.21 it
     * throws a TypeError on every call — verified against this image at all
     * four orientations, with and without hints. So the multi-code case is done
     * by hand: paint over the symbol just read and ask again, until nothing is
     * left. Four passes, because a label with five distinct codes is not a
     * thing and an unbounded loop on a bad binarisation is.
     */
    const results: Array<import('@zxing/library').Result> = [];
    const ctxMask = rotated.getContext('2d')!;
    for (let pass = 0; pass < 4; pass += 1) {
      try {
        const reader = new MultiFormatReader();
        reader.setHints(hints);
        const bitmap = new BinaryBitmap(
          new HybridBinarizer(new HTMLCanvasElementLuminanceSource(rotated)),
        );
        const result = reader.decode(bitmap);
        results.push(result);

        const pts = result.getResultPoints().filter(Boolean);
        if (pts.length === 0) break;
        const xs = pts.map((p) => p.getX());
        const ys = pts.map((p) => p.getY());
        const mx = Math.min(...xs);
        const my = Math.min(...ys);
        const mw = Math.max(...xs) - mx;
        const mh = Math.max(...ys) - my;
        // Generous, and along both axes: a 1D code's points sit on one scan
        // line, so masking only that line leaves the bars behind and the next
        // pass reads the same code again.
        ctxMask.fillStyle = '#fff';
        ctxMask.fillRect(
          mx - mw * 0.15 - 20,
          my - mh * 0.15 - 20,
          mw * 1.3 + 40,
          mh * 1.3 + 40,
        );
      } catch {
        // Nothing more at this orientation — the normal way this loop ends.
        break;
      }
    }

    for (const result of results) {
      const text = result.getText();
      if (!text || seen.has(text)) continue;
      seen.add(text);

      const pts = result.getResultPoints().filter(Boolean).map((p) => unrotate(p.getX(), p.getY()));
      if (pts.length === 0) continue;

      const xs = pts.map((p) => p.x);
      const ys = pts.map((p) => p.y);
      let x0 = Math.min(...xs);
      let y0 = Math.min(...ys);
      let x1 = Math.max(...xs);
      let y1 = Math.max(...ys);

      /**
       * A 1D barcode's result points sit on the ONE scan line that decoded, so
       * the box comes back as a hairline — and, worse, a hairline placed
       * wherever that line happened to fall rather than through the middle of
       * the bars. Measured on the bottle photo: the line landed near the left
       * edge of the code, and a box centred on it sat visibly beside the
       * barcode instead of over it.
       *
       * Nothing in the result says how tall the bars are, so the box is made
       * deliberately GENEROUS around the line rather than tight. The two errors
       * are not symmetric: a zone slightly larger than the code still decodes,
       * while one that clips the bars decodes the reference and then fails
       * every real pack — a fault that only shows up on the line.
       */
      const format = BarcodeFormat[result.getBarcodeFormat()] ?? 'CODE';
      if (!/QR|DATA_MATRIX|AZTEC|PDF_417/.test(format)) {
        const along = Math.max(x1 - x0, y1 - y0);
        const thickness = Math.max(along * 0.9, 0.06);
        if (x1 - x0 >= y1 - y0) {
          const mid = (y0 + y1) / 2;
          y0 = mid - thickness / 2;
          y1 = mid + thickness / 2;
          // A barcode needs its quiet zone to decode; the end points sit on the
          // outermost bars, not outside them.
          const grow = (x1 - x0) * 0.06;
          x0 -= grow;
          x1 += grow;
        } else {
          const mid = (x0 + x1) / 2;
          x0 = mid - thickness / 2;
          x1 = mid + thickness / 2;
          const grow = (y1 - y0) * 0.06;
          y0 -= grow;
          y1 += grow;
        }
      }

      // Pad, because a code checked edge-to-edge fails on a millimetre of
      // camera shift.
      const pad = 0.015;
      x0 = Math.max(0, x0 - pad);
      y0 = Math.max(0, y0 - pad);
      x1 = Math.min(1, x1 + pad);
      y1 = Math.min(1, y1 + pad);

      found.push({
        id: `p${found.length}-code`,
        check: 'code',
        x: x0,
        y: y0,
        w: x1 - x0,
        h: y1 - y0,
        label: codeLabel(format),
        expected: text,
        readText: text,
        confidence: 100,
        reason: `decoded as ${format.replace(/_/g, '-')}${deg ? ` (found with the image turned ${deg}°)` : ''}`,
      });
    }
  }

  return found;
}

/**
 * Group words into the blocks a person would call "a field".
 *
 * Tesseract's own block segmentation is built for documents and treats a whole
 * label panel as one paragraph, which would propose one enormous zone. Words
 * are grouped here instead: onto a line when they share a baseline and sit
 * close horizontally, then lines onto a block when they stack directly under
 * one another. The thresholds are in units of the text's own height, so this
 * behaves the same on a 600 px photo and a 4000 px one.
 */
function groupWords(words: Word[]): Word[][] {
  /*
    Grouped per orientation, then merged.

    A word read at 0° and one read at 90° can sit side by side on the image and
    have nothing to do with each other — one is body text, the other is print
    running up the side of the pack. Grouping them together produces a zone
    spanning both, which is the kind of box that looks deliberate and is wrong.
  */
  const byOrientation = new Map<number, Word[]>();
  for (const w of words) {
    if (w.text.trim().length === 0 || w.conf <= 30) continue;
    const list = byOrientation.get(w.deg) ?? [];
    list.push(w);
    byOrientation.set(w.deg, list);
  }
  return [...byOrientation.values()].flatMap((group) => groupOneOrientation(group));
}

function groupOneOrientation(usable: Word[]): Word[][] {
  if (usable.length === 0) return [];

  const heights = usable.map((w) => w.ry1 - w.ry0).sort((a, b) => a - b);
  const medianH = heights[Math.floor(heights.length / 2)] || 1;

  // Lines: same baseline, small horizontal gap — in the reading frame.
  const byY = [...usable].sort((a, b) => a.ry0 - b.ry0 || a.rx0 - b.rx0);
  const lines: Word[][] = [];
  for (const w of byY) {
    const line = lines.find((l) => {
      const last = l[l.length - 1]!;
      const sameRow = Math.abs((w.ry0 + w.ry1) / 2 - (last.ry0 + last.ry1) / 2) < medianH * 0.6;
      const near = w.rx0 - Math.max(...l.map((x) => x.rx1)) < medianH * 2.5;
      return sameRow && near;
    });
    if (line) line.push(w);
    else lines.push([w]);
  }

  // Blocks: lines stacked under one another with overlapping columns.
  const blocks: Word[][] = [];
  for (const line of lines.sort((a, b) => a[0]!.ry0 - b[0]!.ry0)) {
    const box = rbox(line);
    const block = blocks.find((b) => {
      const bb = rbox(b);
      const gap = box.y0 - bb.y1;
      const overlapping = box.x0 < bb.x1 && box.x1 > bb.x0;
      return overlapping && gap > -medianH && gap < medianH * 1.1;
    });
    if (block) block.push(...line);
    else blocks.push([...line]);
  }
  return blocks;
}

/** Bounding box in the frame the words were read in. */
function rbox(words: Word[]) {
  return {
    x0: Math.min(...words.map((w) => w.rx0)),
    y0: Math.min(...words.map((w) => w.ry0)),
    x1: Math.max(...words.map((w) => w.rx1)),
    y1: Math.max(...words.map((w) => w.ry1)),
  };
}

/**
 * Is this a printed field, or is it OCR hallucinating on a shiny surface?
 *
 * Tuned against a photograph of a curved, transparent bottle — the hardest
 * thing anyone would realistically put in front of this. Left at the obvious
 * threshold (any three letters, confidence over 30) it proposed three zones on
 * that image and all three were nonsense: "EN - =S Orcs D WA gf > | Deen oi",
 * at confidence 51.
 *
 * Nonsense proposals are not a cosmetic problem. Somebody accepting a plausible
 * looking box gets a `match` zone over a reflection, and that zone rejects
 * every good pack until a person works out why. A detector that proposes four
 * correct zones and misses six is useful; one that proposes ten of which four
 * are correct is worse than nothing, because it moves the work from drawing
 * boxes to auditing them.
 *
 * So: real words or real numbers, read confidently, and mostly not punctuation.
 */
function looksLikeAField(block: Word[], text: string): boolean {
  const meanConf = block.reduce((s, w) => s + w.conf, 0) / block.length;
  // Lowered from 65: on a real shipping label — rotated print, many small fields —
  // 65 dropped legible ones and the scan came back short. The transparent-bottle
  // glare that first argued for a high bar read at 51, so 55 still keeps it out,
  // and the alnum/token/stray guards below carry the rest of the weight. A wrong
  // proposal is a dashed box discarded in one click, so the cost of one more is
  // low and the cost of a missed field is a person hunting for what isn't there.
  if (meanConf < 55) return false;

  // Mostly letters and digits — OCR noise on glare is largely punctuation.
  const alnum = text.replace(/[^A-Za-z0-9]/g, '').length;
  if (alnum < 3 || alnum / text.length < 0.5) return false;

  /**
   * At least one token containing a real run of letters or digits.
   *
   * Unanchored on purpose. Anchoring it (`^[A-Za-z]{3,}$`) looked stricter and
   * silently dropped every field with punctuation in it — a web address, an
   * email, a lot code like "2026-A7734". Verified on a flat test label, where
   * "www.nordvikprecision.com" was read perfectly at confidence 96 and then
   * thrown away by this line.
   */
  const tokens = text.split(/\s+/).filter(Boolean);
  if (!tokens.some((t) => /[A-Za-z]{3,}|\d{3,}/.test(t))) return false;

  // A line of mostly one-letter tokens is glare being read as letters.
  const stray = tokens.filter((t) => t.replace(/[^A-Za-z0-9]/g, '').length <= 1).length;
  return stray / tokens.length < 0.5;
}

function bbox(words: Word[]) {
  return {
    x0: Math.min(...words.map((w) => w.x0)),
    y0: Math.min(...words.map((w) => w.y0)),
    x1: Math.max(...words.map((w) => w.x1)),
    y1: Math.max(...words.map((w) => w.y1)),
  };
}

/** Rectangles overlapping by more than half are the same field found twice. */
function overlaps(a: LabelZone, b: LabelZone): boolean {
  const ix = Math.max(0, Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x));
  const iy = Math.max(0, Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y));
  const inter = ix * iy;
  const smaller = Math.min(a.w * a.h, b.w * b.h);
  return smaller > 0 && inter / smaller > 0.5;
}

function classify(text: string): { label: string; check: LabelZone['check']; pattern?: string; reason: string } {
  for (const rule of FIELD_RULES) {
    if (rule.test.test(text)) {
      return { label: rule.label, check: rule.check, pattern: rule.pattern, reason: rule.reason };
    }
  }
  // Fixed print is the safe default: `match` fails loudly on a pack that
  // differs, and a person retypes it to `present` in one click. The reverse
  // mistake — `present` on print that should be identical — passes everything
  // and is never noticed.
  const words = text.split(/\s+/).filter(Boolean).slice(0, 4).join(' ');
  return {
    label: words.length > 2 ? words.slice(0, 40) : 'Printed text',
    check: 'match',
    reason: 'no recognised field type — proposed as fixed print',
  };
}

/**
 * Scan a reference image and propose zones.
 *
 * The image is drawn to a canvas once and both detectors read that, so a large
 * photo is decoded a single time.
 */
export interface Region {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface ScanResult {
  zones: ZoneProposal[];
  /**
   * The crop the text pass actually used — the one a person drew, or, when they
   * drew none, the label the scan found on its own. Returned so the UI can show
   * it: an auto-crop nobody can see is a decision made behind their back, and if
   * it lands wrong the only clue is fields that should be there and aren't.
   */
  usedRegion: Region | null;
  /** True when usedRegion was found automatically rather than supplied. */
  autoCropped: boolean;
}

export async function detectLabelZones(
  imageUrl: string,
  onProgress?: (message: string) => void,
  region?: Region | null,
): Promise<ScanResult> {
  onProgress?.('loading the image');
  const img = await loadImage(imageUrl);

  /*
    The region is why this exists. A reference is often a photo of a pack on a
    bench, three-quarters background, and OCR run over the whole frame reads the
    desk and the wall as confidently as the label — scattered nonsense that
    buries the few real fields. Cropping to the label first is the single change
    that makes the text pass usable on a photograph rather than a flat scan.

    The crop is taken from the ORIGINAL image, not the downscaled working
    canvas: a label filling a fifth of the frame would otherwise arrive already
    thrown away to a few hundred pixels, and the whole point is to give the OCR
    more detail on the part that matters, not less.
  */
  // Cap the working size. Tesseract slows superlinearly on huge photos and
  // gains nothing above roughly 2000 px for print this size — but the ORIGINAL
  // stays untouched, because zones are normalised and the reference image is
  // the one thing that must keep its detail.
  const fullScale = Math.min(1, 2000 / Math.max(img.naturalWidth, img.naturalHeight));
  const full = document.createElement('canvas');
  full.width = Math.round(img.naturalWidth * fullScale);
  full.height = Math.round(img.naturalHeight * fullScale);
  full.getContext('2d')!.drawImage(img, 0, 0, full.width, full.height);

  /*
    Codes are ALWAYS read from the whole frame, even when a region is given.

    They decode reliably at any orientation, so there is nothing to gain by
    restricting them — and a great deal to lose: a person cropping to the print
    to help the OCR would otherwise drop the barcode sitting just outside their
    box, and wonder where it went. The crop is a lens for the unreliable half,
    the text; the reliable half does not need it.
  */
  onProgress?.('looking for barcodes');
  const codes = await detectCodes(full).catch(() => [] as ZoneProposal[]);

  /*
    Find the label itself, when nobody drew a box around it.

    This is the change that removes the manual step. The crop was the fix for a
    photo three-quarters background, but requiring the person to draw it made a
    working feature feel broken — click, get two codes, no idea a second action
    exists. So when no region is supplied, the scan looks for the densest block
    of print and crops to that on its own. An explicit box always wins; this
    only fills the silence.
  */
  const explicit = clampRegion(region);
  const autoResult = explicit ? null : detectLabelRegion(full);
  const auto = autoResult?.region ?? null;
  const effective = explicit ?? auto;
  /*
    A flat label scan fills the frame, so detectLabelRegion returns no crop — but
    the whole image IS the label, and the printed-area detector (gated to a crop
    to avoid boxing the desk behind a bottle) should run on it all the same. That
    case is the difference between finding two codes and finding the seven fields
    a person sees. It is distinguished from "no clear label" so a genuine bench
    photo with no crop still stays off.
  */
  const wholeFrameIsLabel = !effective && (autoResult?.fillsFrame ?? false);

  // Text, on the crop (drawn or found). Taken from the ORIGINAL image so a small
  // label region keeps its detail rather than arriving pre-shrunk.
  const src = effective ?? { x: 0, y: 0, w: 1, h: 1 };
  const sx = src.x * img.naturalWidth;
  const sy = src.y * img.naturalHeight;
  const sw = src.w * img.naturalWidth;
  const sh = src.h * img.naturalHeight;
  const scale = Math.min(1, 2000 / Math.max(sw, sh));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(sw * scale));
  canvas.height = Math.max(1, Math.round(sh * scale));
  canvas.getContext('2d')!.drawImage(img, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);

  onProgress?.('reading the text');
  const words = await readWords(canvas, onProgress);
  const blocks = groupWords(words);

  const textZones: ZoneProposal[] = [];
  for (const block of blocks) {
    const box = bbox(block);
    const text = block
      .sort((a, b) => a.ry0 - b.ry0 || a.rx0 - b.rx0)
      .map((w) => w.text)
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();

    if (!looksLikeAField(block, text)) continue;

    const pad = Math.max(canvas.width, canvas.height) * 0.004;
    const zone: ZoneProposal = {
      id: `p-text-${textZones.length}`,
      ...classify(text),
      x: Math.max(0, (box.x0 - pad) / canvas.width),
      y: Math.max(0, (box.y0 - pad) / canvas.height),
      w: Math.min(1, (box.x1 - box.x0 + pad * 2) / canvas.width),
      h: Math.min(1, (box.y1 - box.y0 + pad * 2) / canvas.height),
      readText: text,
      confidence: Math.round(block.reduce((s, w) => s + w.conf, 0) / block.length),
      pattern: undefined,
      expected: null,
      threshold: null,
    };
    zone.pattern = classify(text).pattern ?? null;

    // The code-overlap dedup is deliberately NOT here: codes are in whole-image
    // coordinates and this zone is still in crop coordinates, so the comparison
    // is only meaningful after the remap below.

    /*
      Four orientations means the same patch of print is offered up to four
      times — once read properly, and up to three times as whatever upside-down
      or sideways nonsense Tesseract made of it. Keep the confident reading and
      drop the rest, rather than showing a person the same field four ways and
      asking them to pick.
    */
    const rival = textZones.findIndex((z) => overlaps(z, zone));
    if (rival >= 0) {
      if (zone.confidence > textZones[rival]!.confidence) textZones[rival] = zone;
      continue;
    }
    textZones.push(zone);
  }

  /*
    Text zones are normalised to the CROP. Map them back to the whole image
    first, because a zone is stored and drawn against the full reference — the
    crop was only ever a lens on where to look, never a change to the coordinate
    system a zone lives in. Codes are already in whole-image coordinates.
  */
  const textInFull = textZones.map((z) => ({
    ...z,
    x: src.x + z.x * src.w,
    y: src.y + z.y * src.h,
    w: z.w * src.w,
    h: z.h * src.h,
  }));

  // Now both are in the same frame, drop any text that landed on a code — the
  // decoded value is the better answer than whatever OCR made of the bars.
  const text = textInFull.filter((z) => !codes.some((k) => overlaps(k, z)));

  /*
    Printed AREAS, found without reading them.

    On a curved, transparent bottle the OCR cannot read most of the print — but
    a person can plainly see WHERE the printed panels are, and for a `match`
    check that is all that is needed: it compares pixels against the reference,
    it never reads a word. So this finds blocks of ink by their contrast and
    boxes them, filling the gap between "two codes" and the dozen fields a
    person sees.

    Gated to a crop. On the whole photo the same detector boxes the desk grain
    and the shelf behind the bottle as readily as the label; inside a crop there
    is nothing but label, so the signal is clean. Without a crop this stays off
    and the scan returns what it can read — the codes and any legible text.
  */
  const printed =
    effective || wholeFrameIsLabel
      ? detectPrintedRegions(canvas)
          .map((z) => ({
            ...z,
            x: src.x + z.x * src.w,
            y: src.y + z.y * src.h,
            w: z.w * src.w,
            h: z.h * src.h,
          }))
          // Anything already found as a code or read as text is the better answer.
          .filter((z) => ![...codes, ...text].some((o) => overlaps(o, z)))
      : [];

  // Biggest first: the fields worth checking are usually the printed panels,
  // and a person accepting from the top gets the important ones first.
  const all = [
    ...codes,
    ...text.sort((a, b) => b.w * b.h - a.w * a.h),
    ...printed.sort((a, b) => b.w * b.h - a.w * a.h),
  ];
  return {
    zones: all.map((z, i) => ({ ...z, id: `p${i}-${z.check}` })),
    usedRegion: effective,
    autoCropped: !explicit && auto != null,
  };
}

/**
 * Find the label in a photo of a pack — the densest block of print.
 *
 * The same contrast-and-darkness test the printed-area detector uses, run over
 * the whole frame: ink has packed light-to-dark transitions where the bench,
 * the wall and clear plastic do not, and requiring a genuinely dark pixel drops
 * the bright reflections a transparent bottle throws. The largest connected
 * cluster of inky cells is the label; its bounding box, padded, is the crop.
 *
 * Returns no crop in two cases that mean different things: `fillsFrame` when the
 * print already spans the frame — a flat label scan, where the whole image is the
 * label and the printed-area detector should still run — and a plain null region
 * with fillsFrame false when there is no dense print to lock onto (a bench photo
 * the person must crop by hand). Both leave OCR on the whole image; only the first
 * lets the printed-area pass run without a crop.
 */
function detectLabelRegion(canvas: HTMLCanvasElement): { region: Region | null; fillsFrame: boolean } {
  const W = canvas.width;
  const H = canvas.height;
  const cell = Math.max(6, Math.round(Math.max(W, H) / 60));
  const cols = Math.max(1, Math.floor(W / cell));
  const rows = Math.max(1, Math.floor(H / cell));
  if (cols < 4 || rows < 4) return { region: null, fillsFrame: false };

  const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
  const { data } = ctx.getImageData(0, 0, W, H);
  const lum = (i: number) => (data[i]! * 299 + data[i + 1]! * 587 + data[i + 2]! * 114) / 1000;

  const score = new Float32Array(cols * rows);
  const darkest = new Float32Array(cols * rows);
  for (let cy = 0; cy < rows; cy += 1) {
    for (let cx = 0; cx < cols; cx += 1) {
      let sum = 0;
      let n = 0;
      let dark = 255;
      const x0 = cx * cell;
      const y0 = cy * cell;
      for (let y = y0; y < y0 + cell && y < H; y += 2) {
        for (let x = x0; x < x0 + cell - 1 && x < W - 1; x += 2) {
          const i = (y * W + x) * 4;
          const l = lum(i);
          sum += Math.abs(l - lum(i + 4));
          if (l < dark) dark = l;
          n += 1;
        }
      }
      score[cy * cols + cx] = n ? sum / n : 0;
      darkest[cy * cols + cx] = dark;
    }
  }

  const sorted = [...score].sort((a, b) => a - b);
  const pct = sorted[Math.floor(sorted.length * 0.75)] ?? 0;
  const threshold = Math.max(pct, 7);

  /*
    The label is the EXTENT of the print, not its densest block.

    "Largest connected cluster" was the obvious first idea and the wrong one: on
    a real label the text lines are separated by whitespace, so they are
    separate clusters, and the single biggest one is the barcode — measured, it
    cropped to the barcode and found nothing else. A label's fields do not
    connect; their bounding box does contain them all.

    So: take the box around every inky cell, but trimmed at the 3rd and 97th
    percentile on each axis, so a stray speck of background texture or one
    reflection the darkness test let through cannot stretch the box across the
    whole frame. On an opaque label on a bench this lands on the label; on a
    transparent bottle, where print shows through from every side, it is
    honestly too big — that is the case a person crops by hand.
  */
  const xs: number[] = [];
  const ys: number[] = [];
  for (let i = 0; i < cols * rows; i += 1) {
    if (score[i]! >= threshold && darkest[i]! < 120) {
      xs.push(i % cols);
      ys.push(Math.floor(i / cols));
    }
  }
  if (xs.length < 8) return { region: null, fillsFrame: false };

  xs.sort((a, b) => a - b);
  ys.sort((a, b) => a - b);
  const at = (arr: number[], q: number) => arr[Math.min(arr.length - 1, Math.floor(arr.length * q))]!;
  const cx0 = at(xs, 0.03);
  const cx1 = at(xs, 0.97) + 1;
  const cy0 = at(ys, 0.03);
  const cy1 = at(ys, 0.97) + 1;

  // Pad the box, in cells, so the outermost print is not clipped.
  const pad = 1.5;
  let x0 = (Math.max(0, cx0 - pad) * cell) / W;
  let y0 = (Math.max(0, cy0 - pad) * cell) / H;
  let x1 = (Math.min(cols, cx1 + pad) * cell) / W;
  let y1 = (Math.min(rows, cy1 + pad) * cell) / H;
  x0 = Math.max(0, x0);
  y0 = Math.max(0, y0);
  x1 = Math.min(1, x1);
  y1 = Math.min(1, y1);

  const w = x1 - x0;
  const h = y1 - y0;
  // Already fills the frame → it is a flat label, not a pack on a bench. No crop
  // to make, but the caller should still run the printed-area pass on the whole
  // image, so this says so rather than hiding behind a bare null.
  if (w > 0.92 && h > 0.92) return { region: null, fillsFrame: true };
  // Implausibly thin → not a label block; don't act on it.
  if (w < 0.06 || h < 0.06) return { region: null, fillsFrame: false };

  return { region: { x: x0, y: y0, w, h }, fillsFrame: false };
}

/**
 * Find blocks of print by contrast, and box them without reading them.
 *
 * Ink has texture — sharp light-to-dark transitions packed together — where a
 * smooth panel, a reflection, or clear plastic does not. The image is divided
 * into a grid; each cell scores how much its neighbours differ from it; cells
 * above the image's own threshold are "inky"; touching inky cells become one
 * region; regions are boxed, merged, filtered and capped.
 *
 * Everything is measured in units of the grid, so it behaves the same on a
 * small crop and a large one, and the threshold is derived from THIS image's
 * own contrast rather than a fixed number that would suit one photo and not the
 * next.
 */
function detectPrintedRegions(canvas: HTMLCanvasElement): ZoneProposal[] {
  const W = canvas.width;
  const H = canvas.height;
  // ~50 cells on the long edge: fine enough to separate two stacked lines,
  // coarse enough that one letter is not its own region.
  const cell = Math.max(6, Math.round(Math.max(W, H) / 50));
  const cols = Math.max(1, Math.floor(W / cell));
  const rows = Math.max(1, Math.floor(H / cell));
  if (cols < 3 || rows < 3) return [];

  const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
  const { data } = ctx.getImageData(0, 0, W, H);
  const lum = (i: number) => (data[i]! * 299 + data[i + 1]! * 587 + data[i + 2]! * 114) / 1000;

  // Per cell, two numbers: how much adjacent pixels differ (contrast, high on
  // print), and the darkest pixel in the cell (low where there is real ink).
  const score = new Float32Array(cols * rows);
  const darkest = new Float32Array(cols * rows);
  for (let cy = 0; cy < rows; cy += 1) {
    for (let cx = 0; cx < cols; cx += 1) {
      let sum = 0;
      let n = 0;
      let dark = 255;
      const x0 = cx * cell;
      const y0 = cy * cell;
      for (let y = y0; y < y0 + cell && y < H; y += 2) {
        for (let x = x0; x < x0 + cell - 1 && x < W - 1; x += 2) {
          const i = (y * W + x) * 4;
          const l = lum(i);
          sum += Math.abs(l - lum(i + 4));
          if (l < dark) dark = l;
          n += 1;
        }
      }
      score[cy * cols + cx] = n ? sum / n : 0;
      darkest[cy * cols + cx] = dark;
    }
  }

  // Threshold from the image itself: the 70th percentile of cell contrast, with
  // a floor so a blank crop does not promote its own faint noise to "ink".
  const sorted = [...score].sort((a, b) => a - b);
  const pct = sorted[Math.floor(sorted.length * 0.7)] ?? 0;
  const threshold = Math.max(pct, 6);

  /*
    Inky = contrast AND genuine dark ink present.

    Contrast alone is not enough on a transparent bottle. The clear base throws
    bright caustic reflections that have every bit as much local contrast as
    print — measured on the real photo, they produced confident boxes on the
    empty base. The difference is that print contains dark ink and a highlight
    does not: requiring the cell to hold a pixel below 120 keeps the blue print
    and drops the glare, which is bright by definition.
  */
  const inky = score.map((s, i) => (s >= threshold && darkest[i]! < 120 ? 1 : 0));
  const seen = new Uint8Array(cols * rows);
  const regions: Array<{ x0: number; y0: number; x1: number; y1: number; cells: number }> = [];
  for (let start = 0; start < inky.length; start += 1) {
    if (!inky[start] || seen[start]) continue;
    const stack = [start];
    seen[start] = 1;
    let minX = cols;
    let minY = rows;
    let maxX = 0;
    let maxY = 0;
    let count = 0;
    while (stack.length) {
      const idx = stack.pop()!;
      const cx = idx % cols;
      const cy = Math.floor(idx / cols);
      count += 1;
      minX = Math.min(minX, cx);
      minY = Math.min(minY, cy);
      maxX = Math.max(maxX, cx);
      maxY = Math.max(maxY, cy);
      for (let dy = -1; dy <= 1; dy += 1) {
        for (let dx = -1; dx <= 1; dx += 1) {
          const nx = cx + dx;
          const ny = cy + dy;
          if (nx < 0 || ny < 0 || nx >= cols || ny >= rows) continue;
          const ni = ny * cols + nx;
          if (inky[ni] && !seen[ni]) {
            seen[ni] = 1;
            stack.push(ni);
          }
        }
      }
    }
    // A single cell is a fleck, not a field.
    if (count >= 3) regions.push({ x0: minX, y0: minY, x1: maxX + 1, y1: maxY + 1, cells: count });
  }

  const proposals: ZoneProposal[] = regions.map((r, i) => ({
    id: `p-ink-${i}`,
    check: 'match',
    x: (r.x0 * cell) / W,
    y: (r.y0 * cell) / H,
    w: ((r.x1 - r.x0) * cell) / W,
    h: ((r.y1 - r.y0) * cell) / H,
    label: 'Printed area',
    readText: '',
    confidence: 0,
    reason: 'a block of print, boxed by its contrast — compared as an image, not read',
    pattern: null,
    expected: null,
    threshold: null,
  }));

  // Merge blocks that touch, drop slivers, cap the count. A proposal list longer
  // than the label is not a help — the largest panels are the ones worth having.
  return mergePrinted(proposals)
    .filter((p) => p.w > 0.02 && p.h > 0.01 && p.w * p.h < 0.55)
    .sort((a, b) => b.w * b.h - a.w * a.h)
    .slice(0, 10);
}

/** Join printed blocks that touch or nearly touch, until nothing else joins. */
function mergePrinted(zones: ZoneProposal[]): ZoneProposal[] {
  const slack = 0.012;
  const out = zones.map((z) => ({ ...z }));
  let merged = true;
  while (merged) {
    merged = false;
    outer: for (let i = 0; i < out.length; i += 1) {
      for (let j = i + 1; j < out.length; j += 1) {
        const a = out[i]!;
        const b = out[j]!;
        const near =
          a.x - slack < b.x + b.w &&
          b.x - slack < a.x + a.w &&
          a.y - slack < b.y + b.h &&
          b.y - slack < a.y + a.h;
        if (!near) continue;
        const x0 = Math.min(a.x, b.x);
        const y0 = Math.min(a.y, b.y);
        a.w = Math.max(a.x + a.w, b.x + b.w) - x0;
        a.h = Math.max(a.y + a.h, b.y + b.h) - y0;
        a.x = x0;
        a.y = y0;
        out.splice(j, 1);
        merged = true;
        break outer;
      }
    }
  }
  return out;
}

/** Keep a region inside the image and refuse a degenerate one. */
function clampRegion(region?: Region | null): Region | null {
  if (!region) return null;
  const x = Math.max(0, Math.min(1, region.x));
  const y = Math.max(0, Math.min(1, region.y));
  const w = Math.max(0, Math.min(1 - x, region.w));
  const h = Math.max(0, Math.min(1 - y, region.h));
  // A crop smaller than a postage stamp is a stray click, not a selection.
  if (w < 0.02 || h < 0.02) return null;
  return { x, y, w, h };
}

/**
 * Prepare an image for OCR: bigger, grey, and with the contrast pulled open.
 *
 * All three matter, and the reason is the photograph this was built against —
 * 480x640, blue print on clear plastic. Handed to Tesseract as-is it read
 * essentially nothing, and it was tempting to call that the limit of OCR on a
 * curved bottle. It was not. The print is legible to a person at that size, so
 * it is legible in principle.
 *
 *  - UPSCALED, because the body text is about eight pixels tall in the original
 *    and Tesseract needs roughly twenty to have a chance. Interpolation adds no
 *    information, but it gives the line-finder something to work with.
 *  - GREY by luminance, so blue-on-clear stops being a colour problem.
 *  - CONTRAST STRETCHED between the 5th and 95th percentile, which is what
 *    turns "slightly darker than the background" into "ink".
 */
function prepareForOcr(source: HTMLCanvasElement, target = 1800): HTMLCanvasElement {
  const scale = Math.max(1, target / Math.max(source.width, source.height));
  const out = document.createElement('canvas');
  out.width = Math.round(source.width * scale);
  out.height = Math.round(source.height * scale);
  const ctx = out.getContext('2d', { willReadFrequently: true })!;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(source, 0, 0, out.width, out.height);

  const img = ctx.getImageData(0, 0, out.width, out.height);
  const d = img.data;

  const histogram = new Uint32Array(256);
  for (let i = 0; i < d.length; i += 4) {
    const g = (d[i]! * 299 + d[i + 1]! * 587 + d[i + 2]! * 114) / 1000;
    d[i] = d[i + 1] = d[i + 2] = g;
    histogram[Math.round(g)]! += 1;
  }

  const total = out.width * out.height;
  let lo = 0;
  let hi = 255;
  let seen = 0;
  for (let v = 0; v < 256; v += 1) {
    seen += histogram[v]!;
    if (seen > total * 0.05) {
      lo = v;
      break;
    }
  }
  seen = 0;
  for (let v = 255; v >= 0; v -= 1) {
    seen += histogram[v]!;
    if (seen > total * 0.05) {
      hi = v;
      break;
    }
  }

  // A flat image (lo ≈ hi) would divide by nothing and turn to noise.
  if (hi - lo > 12) {
    const span = hi - lo;
    for (let i = 0; i < d.length; i += 4) {
      const v = Math.max(0, Math.min(255, ((d[i]! - lo) / span) * 255));
      d[i] = d[i + 1] = d[i + 2] = v;
    }
  }

  ctx.putImageData(img, 0, 0);
  return out;
}

/** Turn a canvas by a right angle, for the passes that read sideways print. */
function rotateCanvas(source: HTMLCanvasElement, deg: number): HTMLCanvasElement {
  if (deg === 0) return source;
  const rad = (deg * Math.PI) / 180;
  const out = document.createElement('canvas');
  out.width = deg % 180 === 0 ? source.width : source.height;
  out.height = deg % 180 === 0 ? source.height : source.width;
  const ctx = out.getContext('2d')!;
  ctx.translate(out.width / 2, out.height / 2);
  ctx.rotate(rad);
  ctx.drawImage(source, -source.width / 2, -source.height / 2);
  return out;
}

/**
 * Read the words, at four orientations.
 *
 * A label is not a page. Deposit text runs up the side of a bottle, "RECYCLE
 * ME" is set vertically, and a pack photographed on a bench can be any way up.
 * Tesseract reads horizontal lines, so print turned ninety degrees is not
 * "read badly" — it is not seen at all, and the upright pass returns silence
 * that looks exactly like a label with nothing on it.
 *
 * Each pass is mapped back to the original frame, so the caller only ever sees
 * words positioned on the image it passed in.
 */
async function readWords(
  canvas: HTMLCanvasElement,
  onProgress?: (message: string) => void,
): Promise<Word[]> {
  const { createWorker } = await import('tesseract.js');
  const worker = await createWorker(['eng', 'nld'], 1, {
    langPath: '/tessdata',
    workerPath: '/tesseract/worker.min.js',
    corePath: '/tesseract',
  });

  const prepared = prepareForOcr(canvas);
  const sx = canvas.width / prepared.width;
  const sy = canvas.height / prepared.height;
  const words: Word[] = [];

  try {
    for (const [i, deg] of [0, 90, 180, 270].entries()) {
      onProgress?.(`reading the text (${i + 1} of 4${deg ? `, turned ${deg}°` : ''})`);
      const rotated = rotateCanvas(prepared, deg);
      const rad = (deg * Math.PI) / 180;

      // Inverse of the draw transform in rotateCanvas — derived from the same
      // numbers rather than special-cased per angle, because a mistake here
      // does not fail loudly, it puts a confident box in the wrong place.
      const back = (x: number, y: number) => {
        if (deg === 0) return { x, y };
        const dx = x - rotated.width / 2;
        const dy = y - rotated.height / 2;
        const cos = Math.cos(-rad);
        const sin = Math.sin(-rad);
        return {
          x: dx * cos - dy * sin + prepared.width / 2,
          y: dx * sin + dy * cos + prepared.height / 2,
        };
      };

      let data;
      try {
        ({ data } = await worker.recognize(rotated, {}, { blocks: true }));
      } catch {
        continue; // one bad orientation must not lose the other three
      }

      for (const block of data.blocks ?? []) {
        for (const para of block.paragraphs ?? []) {
          for (const line of para.lines ?? []) {
            for (const w of line.words ?? []) {
              const a = back(w.bbox.x0, w.bbox.y0);
              const b = back(w.bbox.x1, w.bbox.y1);
              words.push({
                text: w.text,
                conf: w.confidence,
                // Rotation swaps which corner is which; normalise so x0 < x1.
                x0: Math.min(a.x, b.x) * sx,
                y0: Math.min(a.y, b.y) * sy,
                x1: Math.max(a.x, b.x) * sx,
                y1: Math.max(a.y, b.y) * sy,
                deg,
                rx0: w.bbox.x0,
                ry0: w.bbox.y0,
                rx1: w.bbox.x1,
                ry1: w.bbox.y1,
              });
            }
          }
        }
      }
    }
    return words;
  } finally {
    await worker.terminate();
  }
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Could not load the reference image'));
    img.src = url;
  });
}
